import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ReactLenis, useLenis } from "lenis/react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { CartProvider } from "@/context/CartContext";
import { PhaseProvider } from "@/context/PhaseContext";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import Landing from "@/pages/Landing";

const ChapterI = lazy(() => import("@/pages/ChapterI"));
const Archive = lazy(() => import("@/pages/Archive"));
const ArchiveEntry = lazy(() => import("@/pages/ArchiveEntry"));
const SocietyPage = lazy(() => import("@/pages/SocietyPage"));
const About = lazy(() => import("@/pages/About"));
const Journal = lazy(() => import("@/pages/Journal"));
const JournalEntry = lazy(() => import("@/pages/JournalEntry"));
const Legal = lazy(() => import("@/pages/Legal"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const Admin = lazy(() => import("@/pages/Admin"));

const RouteTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => { trackPageView(pathname); }, [pathname]);
  return null;
};

// On every route change, jump the (Lenis-controlled) view to the top.
// Skipped when the URL has a hash, so in-page anchor links still land correctly.
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const lenis = useLenis();
  useEffect(() => {
    if (window.location.hash) return;
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, lenis]);
  return null;
};

function App() {
  useEffect(() => { initAnalytics(); }, []);

  return (
    <HelmetProvider>
      <PhaseProvider>
        <CartProvider>
          <BrowserRouter>
            <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
              <RouteTracker />
              <ScrollToTop />
              <Suspense fallback={<div className="min-h-screen bg-bds-bg" />}>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/chapter-i" element={<ChapterI />} />
                  <Route path="/archive" element={<Archive />} />
                  <Route path="/archive/:slug" element={<ArchiveEntry />} />
                  <Route path="/society" element={<SocietyPage />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/journal" element={<Journal />} />
                  <Route path="/journal/:slug" element={<JournalEntry />} />
                  <Route path="/privacy" element={<Legal page="privacy" />} />
                  <Route path="/terms" element={<Legal page="terms" />} />
                  <Route path="/shipping-returns" element={<Legal page="shipping-returns" />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/payment/cancel" element={<PaymentCancel />} />
                  <Route path="/admin" element={<Admin />} />
                </Routes>
              </Suspense>
            </ReactLenis>
          </BrowserRouter>
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              style: {
                background: "#121215",
                border: "1px solid #2A2A30",
                color: "#F7F5F0",
                borderRadius: 0,
                fontFamily: '"Cabinet Grotesk", sans-serif',
              },
            }}
          />
        </CartProvider>
      </PhaseProvider>
    </HelmetProvider>
  );
}

export default App;
